import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';

import { useDeleteTemplate, useGetTemplates } from '@/modules/templates/hooks/use-templates';
import { Template } from '@/modules/templates/types/templates.types';
import { FileText, Plus, RefreshCw, Upload } from 'lucide-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DeleteDialog } from '../components/delete-dialog';
import { ImportDialog } from '../components/import-dialog';
import { TemplateTable } from '../components/template-table';
import { TemplateCardView } from '../components/template-card-view';
import { useAdminTemplatesUiStore } from '../store/use-admin-templates-ui-store';

const PAGE_SIZE = 10;

export default function TemplatesAdminPage() {
  const { t } = useTranslation();

  // Dialog states
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  // Import dialog state lives in Zustand so the AdminTopbar split-button
  // dropdown can open it on mobile.
  const importOpen = useAdminTemplatesUiStore((s) => s.importDialogOpen);
  const setImportOpen = useAdminTemplatesUiStore((s) => s.setImportDialogOpen);

  // Pagination & search state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const where = useMemo(() => {
    if (!debouncedSearch.trim()) return undefined;
    const query = debouncedSearch.trim();
    return {
      or: [{ Title: { contains: query } }, { Description: { contains: query } }],
    };
  }, [debouncedSearch]);

  // Fetch paginated + filtered templates for table
  const {
    data: templatesResult,
    isLoading,
    isRefetching: isRefreshing,
    refetch,
  } = useGetTemplates({
    paging: { pageNo: page, pageSize: PAGE_SIZE },
    order: { direction: 'DESC', field: 'CreatedDate' },
    ...(where && { where }),
  });

  const templates = templatesResult?.items ?? [];
  const totalCount = templatesResult?.totalCount ?? 0;
  const totalPages: number =
    templatesResult?.totalPages || Math.ceil(totalCount / PAGE_SIZE) || 1;

  // Fetch all templates for stats (unfiltered)
  const { data: allTemplatesResult } = useGetTemplates({
    paging: { pageNo: 1, pageSize: 200 },
    order: { direction: 'DESC', field: 'CreatedDate' },
  });
  const allTemplates = allTemplatesResult?.items ?? [];

  // Delete mutation
  const { mutateAsync: deleteTemplateMutation } = useDeleteTemplate();

  // Handle delete
  const handleOpenDelete = (template: Template) => {
    setSelectedTemplate(template);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;
    await deleteTemplateMutation(selectedTemplate.id);
    setSelectedTemplate(null);
  };

  // Stats (from unfiltered query)
  const availableCount = allTemplates.filter((t) => t.available).length;
  const comingSoonCount = allTemplates.filter((t) => !t.available).length;
  const popularCount = allTemplates.filter((t) => t.popular).length;

  // Refresh button reused in the mobile card-view search toolbar.
  const refreshButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => refetch()}
      disabled={isRefreshing}
      className="shrink-0"
      aria-label={t('ADMIN_TEMPLATES_ACTIONS_REFRESH')}
    >
      <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
    </Button>
  );

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header — buttons are md+ only; on mobile they live in the
          AdminTopbar split-button dropdown (Add Template | ▾). */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl  md:text-3xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ADMIN_TEMPLATES_HEADER_TITLE')}
          </h1>
          <p className="text-sm md:text-base text-globe-grey mt-1">
            {t('ADMIN_TEMPLATES_HEADER_DESCRIPTION')}
          </p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button
            variant="primaryOutline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefreshing}
          >
            <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t('ADMIN_TEMPLATES_ACTIONS_REFRESH')}
          </Button>
          <Button variant="primaryOutline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" />
            Import
          </Button>
          <Button size={'sm'} variant={'primary'}>
            <Link to={`/admin/templates/new`} className="flex items-center gap-1">
              <Plus className="size-4" />
              {t('ADMIN_TEMPLATES_ACTIONS_ADDTEMPLATE')}
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards — always single row (3 cols) so they stay compact on
          phones; padding/icon shrink at the sm breakpoint. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-lime-green/10">
                <FileText className="size-6 text-lime-green" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : availableCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_TEMPLATES_STATS_AVAILABLE')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-[hsl(var(--globe-grey))]/10">
                <FileText className="size-6 text-globe-grey" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : comingSoonCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_TEMPLATES_STATS_COMINGSOON')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-lg bg-selise-blue/10">
                <FileText className="size-6 text-selise-blue" />
              </div>
              <div className="min-w-0">
                <div className="text-lg sm:text-2xl font-bold text-[hsl(var(--fg))]">
                  {isLoading ? <Skeleton className="h-6 w-10 sm:h-8 sm:w-12" /> : popularCount}
                </div>
                <p className="text-xs sm:text-sm text-globe-grey truncate">
                  {t('ADMIN_TEMPLATES_STATS_POPULAR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Desktop: classic data table */}
      <div className="hidden md:block">
        <TemplateTable
          templates={templates}
          pagination={{
            page,
            limit: PAGE_SIZE,
            total: totalCount,
            totalPages,
          }}
          search={search}
          onSearchChange={handleSearchChange}
          onPageChange={setPage}
          onDelete={handleOpenDelete}
          loading={isLoading}
        />
      </div>

      {/* Mobile: card view sharing the same data + handlers. */}
      <div className="md:hidden">
        <TemplateCardView
          templates={templates}
          pagination={{
            page,
            limit: PAGE_SIZE,
            total: totalCount,
            totalPages,
          }}
          search={search}
          onSearchChange={handleSearchChange}
          onPageChange={setPage}
          onDelete={handleOpenDelete}
          searchToolbarAction={refreshButton}
          loading={isLoading}
        />
      </div>

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        template={selectedTemplate}
        onConfirm={handleConfirmDelete}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
}
